import axiosInstance from "../axiosClient";
import { formatDate, formatTime } from "../../utils/calendarValidation";
import { getUserFriendlyError } from "../../utils/errorMessages";

/**
 * Get calendar notes with optional filters
 * @param {string} token - Auth token
 * @param {object} filters - Optional filters { month, startDate, endDate }
 * @returns {Promise<Array>} Array of calendar notes
 */
export const getCalendarNotes = async (token, filters = {}) => {
  try {
    const params = {};

    // Add filters if provided
    if (filters.month) {
      params.month = filters.month; // Format: YYYY-MM
    } else if (filters.startDate && filters.endDate) {
      params.startDate = filters.startDate; // Format: YYYY-MM-DD
      params.endDate = filters.endDate;     // Format: YYYY-MM-DD
    }

    const response = await axiosInstance.get('/calendar/notes', {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch calendar notes', error);

    // Handle rate limiting
    if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }

    // Handle validation errors
    if (error.response?.status === 400) {
      const errorMessage = getUserFriendlyError(error);
      throw new Error(errorMessage);
    }

    // For other errors, return empty array to maintain compatibility
    return [];
  }
};

/**
 * Edit an existing calendar note
 * @param {number} noteId - Note ID
 * @param {object} updatedData - { date, startTime, description, operationId }
 * @param {string} token - Auth token
 * @returns {Promise<object>} Updated note
 */
export const editCalendarNote = async (noteId, updatedData, token) => {
  try {
    // Validate and format data before sending
    const payload = {};

    if (updatedData.date !== undefined) {
      // Ensure date is in YYYY-MM-DD format
      payload.date = formatDate(updatedData.date);
    }

    if (updatedData.startTime !== undefined) {
      // Ensure time is in HH:mm format (24-hour)
      payload.startTime = formatTime(updatedData.startTime);
    }

    if (updatedData.description !== undefined) {
      // Trim and limit to 1000 characters
      payload.description = updatedData.description.trim().substring(0, 1000);
    }

    if (updatedData.operationId !== undefined) {
      payload.operationId = updatedData.operationId;
    }

    const response = await axiosInstance.put(`/calendar/notes/${noteId}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update note:', error);

    // Handle specific error cases
    if (error.response?.status === 404) {
      throw new Error('Note not found or you do not have permission to edit it');
    }

    if (error.response?.status === 403) {
      throw new Error('You do not have permission to link this operation');
    }

    if (error.response?.status === 429) {
      throw new Error('Too many update requests. Please wait a moment.');
    }

    if (error.response?.status === 400) {
      const errorMessage = getUserFriendlyError(error);
      throw new Error(errorMessage);
    }

    throw new Error('Failed to update note');
  }
};

/**
 * Create a new calendar note
 * @param {object} noteData - { date, startTime, description, operationId }
 * @param {string} token - Auth token
 * @returns {Promise<object>} Created note
 */
export const createCalendarNote = async (noteData, token) => {
  try {
    // Validate and format data
    const payload = {
      date: formatDate(noteData.date),
      description: noteData.description.trim().substring(0, 1000)
    };

    // Optional fields
    if (noteData.startTime) {
      payload.startTime = formatTime(noteData.startTime);
    }

    if (noteData.operationId) {
      payload.operationId = noteData.operationId;
    }

    const response = await axiosInstance.post('/calendar/notes', payload, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create note:', error);

    if (error.response?.status === 403) {
      throw new Error('You do not have permission to link this operation');
    }

    if (error.response?.status === 429) {
      throw new Error('Too many notes created. Please wait a moment.');
    }

    if (error.response?.status === 400) {
      const errorMessage = getUserFriendlyError(error);
      throw new Error(errorMessage);
    }

    throw new Error('Failed to create note');
  }
};

/**
 * Creates a calendar note linked to a specific operation.
 * Uses the same endpoint but includes operationId in the payload.
 * @param {object} noteData - { date, startTime, description, operationId }
 * @param {string} token - Auth token
 * @returns {Promise<object>} Created note
 */
export const createOperationNote = async (noteData, token) => {
  // Reuse createCalendarNote since it now handles operationId
  return createCalendarNote(noteData, token);
};

/**
 * Delete a calendar note
 * @param {number} noteId - Note ID to delete
 * @param {string} token - Auth token
 * @returns {Promise<object>} Success message
 */
export const deleteCalendarNote = async (noteId, token) => {
  try {
    const response = await axiosInstance.delete(`/calendar/notes/${noteId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to delete note:', error);

    if (error.response?.status === 404) {
      throw new Error('Note not found or you do not have permission to delete it');
    }

    if (error.response?.status === 429) {
      throw new Error('Too many delete requests. Please wait a moment.');
    }

    if (error.response?.status === 400) {
      throw new Error('Invalid note ID');
    }

    throw new Error('Failed to delete note');
  }
};
